import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Check, X, RotateCcw, Loader2 } from 'lucide-react';

export default function AIAssistField({ value, onChange, type = 'text', placeholder, rows, label, authToken, theme }) {
  const [isOpen, setIsOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [rewrittenText, setRewrittenText] = useState('');
  const [error, setError] = useState('');
  const popoverRef = useRef(null);
  const isDark = theme === 'dark';

  // Close popover when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
        setRewrittenText('');
        setError('');
        setPrompt('');
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleRewrite = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/chat/editor/ai-assist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          text: value || '',
          instruction: prompt
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setRewrittenText(data.rewritten);
      } else {
        setError(data.error || 'Failed to rewrite text.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    onChange(rewrittenText);
    setIsOpen(false);
    setRewrittenText('');
    setPrompt('');
  };

  const handleReject = () => {
    setIsOpen(false);
    setRewrittenText('');
    setPrompt('');
  };

  const handleTryAgain = () => {
    setRewrittenText('');
    handleRewrite();
  };

  return (
    <div style={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column' }}>
      {label && (
        <label style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          color: 'var(--text-secondary)',
          marginBottom: '6px',
          display: 'block'
        }}>
          {label}
        </label>
      )}

      <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
        {type === 'textarea' ? (
          <textarea
            className="p-input"
            rows={rows || 3}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder || ''}
            style={{ width: '100%', paddingRight: '36px' }}
          />
        ) : (
          <input
            type={type}
            className="p-input"
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder || ''}
            style={{ width: '100%', paddingRight: '36px' }}
          />
        )}

        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            position: 'absolute',
            right: '8px',
            top: type === 'textarea' ? '12px' : '50%',
            transform: type === 'textarea' ? 'none' : 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            color: isOpen ? 'var(--brand)' : 'var(--text-muted)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4px',
            borderRadius: '50%',
            transition: 'all 0.15s ease',
          }}
          title="AI Assist"
          onMouseEnter={e => e.currentTarget.style.color = 'var(--brand)'}
          onMouseLeave={e => { if (!isOpen) e.currentTarget.style.color = 'var(--text-muted)'; }}
        >
          <Sparkles size={16} className={isOpen ? "animate-pulse" : ""} />
        </button>
      </div>

      {isOpen && (
        <div
          ref={popoverRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 9999,
            marginTop: '6px',
            padding: '14px',
            borderRadius: '12px',
            background: isDark ? 'rgba(20, 20, 25, 0.95)' : '#ffffff',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 10px 30px -5px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
            animation: 'slideDown 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards'
          }}
        >
          {!rewrittenText ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="e.g. make this more impactful, add metrics..."
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)',
                    fontSize: '12px',
                    outline: 'none'
                  }}
                  onKeyDown={e => { if (e.key === 'Enter') handleRewrite(); }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleRewrite}
                  disabled={loading || !prompt.trim()}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    background: 'var(--brand)',
                    color: '#ffffff',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: (!prompt.trim() || loading) ? 0.5 : 1
                  }}
                >
                  {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  Rewrite
                </button>
              </div>
              {error && <span style={{ fontSize: '11px', color: '#ef4444' }}>{error}</span>}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Diff View */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                fontSize: '12px',
                maxHeight: '180px',
                overflowY: 'auto',
                paddingRight: '4px'
              }}>
                <div style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  background: isDark ? 'rgba(239, 68, 68, 0.05)' : 'rgba(239, 68, 68, 0.03)',
                  border: isDark ? '1px solid rgba(239, 68, 68, 0.15)' : '1px solid rgba(239, 68, 68, 0.1)',
                  color: isDark ? '#fca5a5' : '#ef4444',
                  textDecoration: 'line-through'
                }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px', textDecoration: 'none', display: 'block' }}>Original</div>
                  {value || '(Empty)'}
                </div>

                <div style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  background: isDark ? 'rgba(16, 185, 129, 0.07)' : 'rgba(16, 185, 129, 0.04)',
                  border: isDark ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid rgba(16, 185, 129, 0.15)',
                  color: isDark ? '#34d399' : '#10b981'
                }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px', display: 'block' }}>AI Suggestion</div>
                  {rewrittenText}
                </div>
              </div>

              {/* Chips Buttons */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleReject}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                    border: 'none', color: 'var(--text-secondary)', cursor: 'pointer'
                  }}
                >
                  <X size={11} /> Reject
                </button>
                <button
                  type="button"
                  onClick={handleTryAgain}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                    background: isDark ? 'rgba(124, 58, 237, 0.1)' : 'rgba(124, 58, 237, 0.05)',
                    border: 'none', color: 'var(--brand)', cursor: 'pointer'
                  }}
                >
                  <RotateCcw size={11} /> Try again
                </button>
                <button
                  type="button"
                  onClick={handleAccept}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                    background: 'var(--brand)', border: 'none', color: '#ffffff', cursor: 'pointer'
                  }}
                >
                  <Check size={11} /> Accept
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
